import type { Theme } from "./types";

/**
 * Paleta por tema para la web.
 *
 * Define los tokens RGB que se inyectan como variables CSS en el contenedor
 * de la invitación. Esto permite que cada invitación se "vista" con su tema
 * sin tocar utilidades de Tailwind (que referencian las mismas variables).
 *
 * Equivalente al `ThemeBackground` de iOS.
 */
export interface ThemePalette {
  /** RGB triplete "r,g,b" para `--bg`, `--surface`, etc. */
  bg: string;
  surface: string;
  surfaceElevated: string;
  text: string;
  subtext: string;
  accent: string;
  accentStrong: string;
  divider: string;
  /** Color de los "blobs" decorativos del fondo. */
  blobA: string;
  blobB: string;
  /** Para temas claros, la tarjeta base es blanca y el texto oscuro. */
  isLight?: boolean;
}

export const themePalette: Record<Theme, ThemePalette> = {
  apologyLetter: {
    bg: "252,224,232",
    surface: "255,251,248",
    surfaceElevated: "255,255,255",
    text: "58,31,42",
    subtext: "119,82,96",
    accent: "198,86,119",
    accentStrong: "136,73,126",
    divider: "226,178,192",
    blobA: "238,122,158",
    blobB: "188,142,223",
    isLight: true
  },
  romantic: {
    bg: "246,220,228",
    surface: "255,248,249",
    surfaceElevated: "255,255,255",
    text: "45,28,38",
    subtext: "109,82,95",
    accent: "201,95,111",
    accentStrong: "143,65,88",
    divider: "227,181,197",
    blobA: "255,216,181",
    blobB: "226,188,232",
    isLight: true
  },
  velvet: {
    bg: "250,241,246",
    surface: "255,252,253",
    surfaceElevated: "255,255,255",
    text: "55,29,40",
    subtext: "114,84,94",
    accent: "170,98,126",
    accentStrong: "122,52,86",
    divider: "225,194,205",
    blobA: "229,166,185",
    blobB: "150,72,101",
    isLight: true
  },
  luxuryDark: {
    bg: "10,8,8",
    surface: "255,255,255",
    surfaceElevated: "255,255,255",
    text: "255,255,255",
    subtext: "255,255,255",
    accent: "199,160,77",
    accentStrong: "224,190,110",
    divider: "255,255,255",
    blobA: "199,160,77",
    blobB: "77,58,30"
  },
  playful: {
    bg: "18,10,34",
    surface: "255,255,255",
    surfaceElevated: "255,255,255",
    text: "255,255,255",
    subtext: "255,255,255",
    accent: "255,99,132",
    accentStrong: "255,135,162",
    divider: "255,255,255",
    blobA: "255,99,132",
    blobB: "140,89,242"
  },
  funnyChaos: {
    bg: "18,10,34",
    surface: "255,255,255",
    surfaceElevated: "255,255,255",
    text: "255,255,255",
    subtext: "255,255,255",
    accent: "255,99,132",
    accentStrong: "255,135,162",
    divider: "255,255,255",
    blobA: "255,99,132",
    blobB: "140,89,242"
  },
  minimal: {
    bg: "238,238,241",
    surface: "255,255,255",
    surfaceElevated: "255,255,255",
    text: "26,26,30",
    subtext: "26,26,30",
    accent: "90,90,100",
    accentStrong: "60,60,70",
    divider: "0,0,0",
    blobA: "180,180,190",
    blobB: "150,150,160",
    isLight: true
  },
  cinematic: {
    bg: "10,12,18",
    surface: "255,255,255",
    surfaceElevated: "255,255,255",
    text: "255,255,255",
    subtext: "255,255,255",
    accent: "86,138,196",
    accentStrong: "120,170,224",
    divider: "255,255,255",
    blobA: "86,138,196",
    blobB: "140,77,46"
  },
  elegant: {
    bg: "20,18,26",
    surface: "255,255,255",
    surfaceElevated: "255,255,255",
    text: "255,255,255",
    subtext: "255,255,255",
    accent: "138,118,196",
    accentStrong: "168,150,220",
    divider: "255,255,255",
    blobA: "138,118,196",
    blobB: "56,51,102"
  },
  classic: {
    bg: "20,18,26",
    surface: "255,255,255",
    surfaceElevated: "255,255,255",
    text: "255,255,255",
    subtext: "255,255,255",
    accent: "138,118,196",
    accentStrong: "168,150,220",
    divider: "255,255,255",
    blobA: "138,118,196",
    blobB: "56,51,102"
  },
  festive: {
    bg: "26,13,46",
    surface: "255,255,255",
    surfaceElevated: "255,255,255",
    text: "255,255,255",
    subtext: "255,255,255",
    accent: "242,140,64",
    accentStrong: "248,170,110",
    divider: "255,255,255",
    blobA: "242,140,64",
    blobB: "77,77,217"
  },
  cosmic: {
    bg: "10,5,30",
    surface: "255,255,255",
    surfaceElevated: "255,255,255",
    text: "255,255,255",
    subtext: "255,255,255",
    accent: "140,89,242",
    accentStrong: "170,120,255",
    divider: "255,255,255",
    blobA: "140,89,242",
    blobB: "217,89,189"
  }
};

/**
 * Convierte una paleta a un objeto de CSS variables para `style`.
 *
 * El `as React.CSSProperties` se aplica en el punto de uso: TS no admite
 * claves con `--` como CSSProperties "puras", así que devolvemos un record
 * de strings y lo "ensanchamos" al aplicarlo.
 */
export function themeToCssVars(p: ThemePalette): Record<string, string> {
  return {
    "--bg": p.bg,
    "--surface": p.surface,
    "--surface-elevated": p.surfaceElevated,
    "--text": p.text,
    "--subtext": p.subtext,
    "--accent": p.accent,
    "--accent-strong": p.accentStrong,
    "--divider": p.divider,
    "--blob-a": p.blobA,
    "--blob-b": p.blobB
  };
}

/** Etiqueta legible para UI. */
export const themeLabel: Record<Theme, string> = {
  apologyLetter: "Apology Letter",
  romantic: "Romantic",
  velvet: "Velvet",
  luxuryDark: "Luxury dark",
  playful: "Cute playful",
  funnyChaos: "Funny chaos",
  minimal: "Minimal clean",
  cinematic: "Cinematic",
  elegant: "Elegant",
  classic: "Classic",
  festive: "Festive",
  cosmic: "Cosmic"
};
